import {messages} from '@smartmixin/i18n';
import {Box, Divider, Link, Typography} from '@mui/material';
import {Clock, Desktop, DeviceMobile, LinkSimple} from '@phosphor-icons/react';
import {visuallyHidden} from '@mui/utils';
import {ReactNode, useId, useLayoutEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import ReactMarkdown, {Components} from 'react-markdown';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import {getReadingTime} from '../constants';
import {mergeRelatedIntoBody} from '../utils/relatedSection';
import {useArticles} from '../hooks/useArticles';
import {Article} from '../types';
import {platformTabsPlugin} from '../utils/platformTabsPlugin';
import PlatformTabs, {PlatformTab} from './PlatformTabs';
import {
  flattenNodeText,
  HEADING_SCROLL_OFFSET,
  scrollToHeading,
  slugifyHeading,
} from '../utils/headings';
import {wikiLinkPlugin} from '../utils/wikiLinkPlugin';
import ArticleFeedback from './ArticleFeedback';
import ArticleLink from './ArticleLink';
import HelpImage from './HelpImage';
import RelatedArticles from './RelatedArticles';

type ArticleViewProps = {
  article: Article;
};

// Custom component type for article-link
type ArticleLinkComponentProps = {
  slug: string;
  displayText: string;
};

// Extend Components type to include custom help-center elements
type ExtendedComponents = Components & {
  'article-link'?: React.ComponentType<ArticleLinkComponentProps>;
  'platform-tabs'?: React.ComponentType<{children?: ReactNode}>;
  'platform-tab'?: React.ComponentType<{
    platform: 'web' | 'mobile';
    children?: ReactNode;
  }>;
};

type AnchoredHeadingProps = {
  level: 2 | 3 | 4;
  children?: ReactNode;
};

// h2/h3/h4 with a stable anchor id (matching extractHeadings) and a
// hover-revealed permalink, so support replies can deep-link to a section.
const AnchoredHeading = ({level, children}: AnchoredHeadingProps) => {
  const {formatMessage} = useIntl();
  const id = slugifyHeading(flattenNodeText(children));
  const labelId = useId();

  return (
    // The permalink follows the heading rather than sitting in it, so the
    // heading's name is its text alone; the link is named "Link to section"
    // plus the heading (a hidden span and the heading itself).
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        mt: level === 2 ? 4 : level === 3 ? 3 : 2.5,
        mb: 1,
        '&:hover .heading-anchor, & .heading-anchor:focus-visible': {
          opacity: 1,
        },
      }}>
      <Typography
        id={id || undefined}
        variant={HEADING_VARIANT[level]}
        component={`h${level}`}
        sx={(theme) => ({
          m: 0,
          color: 'text.primary',
          fontWeight: theme.typography.title?.fontWeight,
          scrollMarginTop: `${HEADING_SCROLL_OFFSET}px`,
        })}>
        {children}
      </Typography>
      {id && (
        <Link
          href={`#${id}`}
          className="heading-anchor"
          aria-labelledby={`${labelId} ${id}`}
          onClick={(event) => {
            event.preventDefault();
            scrollToHeading(id);
          }}
          sx={{
            display: 'inline-flex',
            alignSelf: 'center',
            color: 'text.secondary',
            opacity: {xs: 1, md: 0},
            transition: 'opacity 150ms ease',
            '&:hover': {color: 'text.primary'},
          }}>
          <Box component="span" id={labelId} sx={visuallyHidden}>
            {formatMessage(messages.helpCenter.linkToSection)}
          </Box>
          <LinkSimple aria-hidden size={16} />
        </Link>
      )}
    </Box>
  );
};

/**
 * A markdown table in a box that scrolls sideways when the table is wider
 * than the column. While it does, the box takes focus so a keyboard can
 * scroll it (axe scrollable-region-focusable).
 */
const ScrollTable = ({children}: {children?: ReactNode}) => {
  const {formatMessage} = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [section, setSection] = useState('');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }
    const measure = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    measure();
    // The heading the table sits under names it.
    const headings = [...document.querySelectorAll('h1, h2, h3, h4')].filter(
      (h) => h.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING,
    );
    setSection(headings[headings.length - 1]?.textContent?.trim() ?? '');
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    // The box and the table: a table that grows (fonts, images) inside a
    // box of constant width changes the overflow without resizing the box.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (tableRef.current) observer.observe(tableRef.current);
    return () => observer.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      data-testid="help-table-scroll"
      // Focusable only while it scrolls; then a named region, so the stop
      // says what it is ("Specifications table, scrolls horizontally").
      tabIndex={overflow ? 0 : undefined}
      role={overflow ? 'region' : undefined}
      aria-label={
        overflow
          ? formatMessage(messages.reports.tableScrollRegion, {
              title: formatMessage(messages.charts.tableLabel, {
                chart: section,
              }),
            })
          : undefined
      }
      sx={{
        overflowX: 'auto',
        mt: 1,
        mb: 3,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '&&:focus-visible': {outlineOffset: '-2px'},
        '& tbody tr:last-of-type > td': {borderBottom: 0},
      }}>
      <Box
        component="table"
        ref={tableRef}
        sx={{width: '100%', borderCollapse: 'collapse'}}>
        {children}
      </Box>
    </Box>
  );
};

const HEADING_VARIANT = {2: 'h5', 3: 'h6', 4: 'title'} as const;

/** smartmixin.io itself, written as an absolute URL in some articles. */
const OWN_SITE = /^https?:\/\/(www\.)?smartmixin\.io(?=\/|$)/;

/**
 * Links in the body: ink and underlined. Other sites open in a new tab;
 * links to smartmixin.io stay in the app (root-relative, same tab).
 */
const MarkdownLink = ({
  href = '',
  children,
  node: _node,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  node?: unknown;
  children?: ReactNode;
}) => {
  const own = OWN_SITE.test(href);
  const to = own ? href.replace(OWN_SITE, '') || '/' : href;
  const external = !own && /^https?:\/\//.test(href);
  return (
    <Link
      {...rest}
      href={to}
      underline="always"
      sx={{color: 'ink.main'}}
      {...(external && {target: '_blank', rel: 'noopener noreferrer'})}>
      {children}
    </Link>
  );
};

const BODY = {
  typography: 'body1',
  color: 'text.primary',
  lineHeight: 1.7,
} as const;

const ArticleView = ({article}: ArticleViewProps) => {
  const {formatMessage} = useIntl();
  const {getRelatedArticles} = useArticles();
  // Most articles end with their own "## Related Articles" section (links
  // with a line of context each); the frontmatter list is only appended
  // where the body has none, so the page never carries both.
  // Most articles end with their own "Related Articles" section. The
  // frontmatter `related:` slugs it does not link join that section; only an
  // article without one gets the generated list (never two sections).
  const {content, hasSection} = mergeRelatedIntoBody(
    article.content,
    article.relatedSlugs ?? [],
    [formatMessage(messages.helpCenter.relatedArticles)],
  );
  const relatedArticles = hasSection ? [] : getRelatedArticles(article);
  const readingTime = getReadingTime(article.content);

  const components: ExtendedComponents = {
    h1: ({children}) => (
      <Typography
        variant="h1"
        component="h1"
        sx={{color: 'text.primary', mb: 1.5}}>
        {children}
      </Typography>
    ),
    h2: ({children}) => <AnchoredHeading level={2}>{children}</AnchoredHeading>,
    h3: ({children}) => <AnchoredHeading level={3}>{children}</AnchoredHeading>,
    h4: ({children}) => <AnchoredHeading level={4}>{children}</AnchoredHeading>,
    p: ({children}) => (
      <Typography component="p" sx={{...BODY, mb: 2}}>
        {children}
      </Typography>
    ),
    a: (props) => <MarkdownLink {...props} />,
    ul: ({children}) => (
      <Box component="ul" sx={{pl: 3, mt: 0, mb: 2}}>
        {children}
      </Box>
    ),
    ol: ({children}) => (
      <Box component="ol" sx={{pl: 3, mt: 0, mb: 2}}>
        {children}
      </Box>
    ),
    li: ({children}) => (
      <Typography component="li" sx={{...BODY, mb: 0.5}}>
        {children}
      </Typography>
    ),
    // react-markdown wraps a fenced block's <code> in this <pre>; the inline
    // `code` style below is reset inside it.
    pre: ({children}) => (
      <Box
        component="pre"
        sx={{
          m: 0,
          mb: 2,
          p: 1.5,
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          typography: 'data',
          color: 'text.primary',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          '& > code': {p: 0, border: 0, bgcolor: 'transparent'},
        }}>
        {children}
      </Box>
    ),
    code: ({children}) => (
      <Box
        component="code"
        sx={{
          px: 0.5,
          py: 0.125,
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: '4px',
          typography: 'data',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          color: 'text.primary',
        }}>
        {children}
      </Box>
    ),
    // "> **Tip**: …" / "> **Warning**: …": the bold label says which; the
    // bar and the page surface set it apart from the body.
    blockquote: ({children}) => (
      <Box
        component="blockquote"
        sx={{
          m: 0,
          my: 2,
          px: 2,
          py: 1.25,
          borderLeft: 3,
          borderColor: 'border.control',
          bgcolor: 'background.default',
          borderRadius: '0 8px 8px 0',
          '& > p:last-child': {mb: 0},
        }}>
        {children}
      </Box>
    ),
    // A wide table scrolls inside its own box rather than widening the page.
    table: ({children}) => <ScrollTable>{children}</ScrollTable>,
    thead: ({children}) => (
      <Box component="thead" sx={{bgcolor: 'background.default'}}>
        {children}
      </Box>
    ),
    th: ({children}) => (
      <Box
        component="th"
        sx={(theme) => ({
          px: 1.5,
          py: 1,
          textAlign: 'left',
          typography: 'caption',
          fontWeight: theme.typography.title?.fontWeight,
          color: 'text.secondary',
          borderBottom: 1,
          borderColor: 'divider',
          verticalAlign: 'bottom',
        })}>
        {children}
      </Box>
    ),
    td: ({children}) => (
      <Box
        component="td"
        sx={{
          px: 1.5,
          py: 1,
          typography: 'body2',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          verticalAlign: 'top',
        }}>
        {children}
      </Box>
    ),
    // Screenshots/illustrations (see HelpImage for the path conventions)
    img: ({src, alt}) => (
      <HelpImage src={typeof src === 'string' ? src : undefined} alt={alt} />
    ),
    // Custom component for wiki-links
    'article-link': ({slug, displayText}: ArticleLinkComponentProps) => (
      <ArticleLink slug={slug} displayText={displayText} />
    ),
    // Synced web/mobile tab groups (:::web / :::mobile directives)
    'platform-tabs': PlatformTabs,
    'platform-tab': PlatformTab,
  };

  return (
    <Box>
      {/* Article metadata */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
          color: 'text.secondary',
        }}>
        <Typography
          variant="caption"
          sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
          <Clock aria-hidden size={14} />
          {formatMessage(messages.helpCenter.minRead, {
            minutes: readingTime,
          })}
        </Typography>
        {/* Availability badge for single-platform features */}
        {article.platforms?.length === 1 && (
          <Typography
            variant="caption"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              border: 1,
              borderColor: 'border.control',
              borderRadius: '4px',
              color: 'text.primary',
            }}>
            {article.platforms[0] === 'mobile' ? (
              <DeviceMobile aria-hidden size={14} />
            ) : (
              <Desktop aria-hidden size={14} />
            )}
            {formatMessage(
              article.platforms[0] === 'mobile'
                ? messages.helpCenter.platformMobileOnly
                : messages.helpCenter.platformWebOnly,
            )}
          </Typography>
        )}
      </Box>

      {/* Article content */}
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkDirective,
          platformTabsPlugin,
          wikiLinkPlugin,
        ]}
        components={components as Components}>
        {content}
      </ReactMarkdown>

      {/* Feedback section */}
      <ArticleFeedback articleId={article.id} />

      {/* Divider before related articles */}
      {relatedArticles.length > 0 && <Divider sx={{my: 4}} />}

      {/* Related articles */}
      <RelatedArticles articles={relatedArticles} />
    </Box>
  );
};

export default ArticleView;
